/* ─────────────────────────────────────────────────────────────
   FINAL VERDICT — the Summary tab's one-line answer, with its working (v3).

   Conviction (FA rating + true value / analyst target) × timing (trend, momentum)
   on a decision matrix, with the vetoes, sized by implied volatility, a print
   inside the expiry blocking new positions. Method, thresholds and all words live
   in utils/finalRating.js (pure, mirrored in the bot); this file fetches the four
   payloads and renders.

   Null rule: a missing leg is shown as such and the method degrades as the
   rules say; no FA grade → the card says so and gives no label. Never "N/A".
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip as MuiTooltip } from "@mui/material";
import { getStockSummary, getFairValueChart, getEarningsReview, getFactorRatings } from "../../../api/stocks_api";
import { computeFinalRating, VERSION } from "../../../utils/finalRating";
import { formatCurrency } from "../../../utils/currency";

const TONE = { "Strong Buy": "var(--color-success)", Buy: "var(--color-success)", Hold: "var(--color-text-secondary)", Sell: "var(--color-error)", "Strong Sell": "var(--color-error)" };
const WARN = "var(--color-warning, #f59e0b)";
const scoreTone = (s) => (s > 0 ? "var(--color-success)" : s < 0 ? "var(--color-error)" : "var(--color-text-muted)");
const sgn = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}`);
const unwrap = (res) => (res?.success ? (res.data?.response_data ?? res.data) : null);

const Chip = ({ children, tone }) => (
  <span style={{ fontSize: "var(--font-size-xs)", fontWeight: 600, padding: "2px 8px", borderRadius: 999, border: `1px solid ${tone || "var(--color-border-primary)"}`, color: tone || "var(--color-text-secondary)" }}>{children}</span>
);

/* a −2…+2 gauge for one leg */
const Gauge = ({ title, value, hint }) => {
  const x = value == null ? 50 : ((Math.max(-2, Math.min(2, value)) + 2) / 4) * 100;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <MuiTooltip title={hint}><span style={{ fontSize: "var(--font-size-xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-muted)" }}>{title}</span></MuiTooltip>
        <span style={{ fontSize: "var(--font-size-sm)", fontWeight: 800, color: value == null ? "var(--color-text-muted)" : scoreTone(value), fontVariantNumeric: "tabular-nums" }}>{value == null ? "not readable" : sgn(value)}</span>
      </div>
      <div style={{ position: "relative", height: 6, borderRadius: 3, marginTop: 6, background: "linear-gradient(90deg, var(--color-error) 0%, var(--color-border-primary) 50%, var(--color-success) 100%)", opacity: value == null ? 0.35 : 1 }}>
        {value != null ? <div style={{ position: "absolute", left: `calc(${x}% - 6px)`, top: -3, width: 12, height: 12, borderRadius: 6, background: "var(--color-text-primary)", border: "2px solid var(--color-surface)", boxShadow: "0 0 0 1px var(--color-border-primary)" }} /> : null}
      </div>
    </div>
  );
};

const Row = ({ name, score, reason, onClick }) => (
  <div onClick={onClick} style={{ cursor: onClick ? "pointer" : "default", marginTop: 6 }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: "var(--font-size-xs)" }}>
      <span style={{ fontWeight: 600, color: "var(--color-text-secondary)" }}>{name}</span>
      {score !== undefined ? <span style={{ fontWeight: 700, color: scoreTone(score), fontVariantNumeric: "tabular-nums" }}>{sgn(score)}</span> : null}
    </div>
    <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-muted)", lineHeight: 1.4 }}>{reason}</div>
  </div>
);

export const FinalVerdict = ({ ticker }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, result: null });

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setState({ loading: true, result: null });
    (async () => {
      const [sum, fv, er, fr] = await Promise.all([getStockSummary(ticker), getFairValueChart(ticker), getEarningsReview(ticker), getFactorRatings(ticker)]);
      if (cancelled) return;
      const summary = unwrap(sum), fair = unwrap(fv), review = unwrap(er), factors = unwrap(fr);
      setState({ loading: false, result: summary ? computeFinalRating(summary, fair, review, factors, ticker) : null });
    })();
    return () => { cancelled = true; };
  }, [ticker]);

  if (state.loading) return <div className="animate-pulse" style={{ height: 160, backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)" }} />;
  const r = state.result;
  if (!r) return null;
  const go = (slug) => () => navigate(`/stocks/${ticker}/details/${slug}`);
  const tone = r.label ? TONE[r.label] : "var(--color-text-muted)";
  const shown = r.label ? r.label + (r.sublabel && r.label === "Hold" ? ` · ${r.sublabel}` : "") : null;

  return (
    <div style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-lg)", padding: "var(--spacing-lg)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ fontSize: "var(--font-size-lg)", fontWeight: 700, color: "var(--color-text-primary)" }}>Final verdict</span>
        <span style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-muted)" }}>conviction × timing · sized by implied volatility · {VERSION} · a method without a track record yet</span>
      </div>

      {r.insufficient ? (
        <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)" }}>{r.reason}</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: "var(--font-size-2xl, 1.6rem)", fontWeight: 800, color: tone, letterSpacing: "-0.01em" }}>{shown}</span>
            <MuiTooltip title="Agreement between quality, valuation and timing (dead band ±0.25). Trend agreeing with an A/B momentum factor is not counted twice. A missing input, an unreliable valuation or incomplete data caps it."><span><Chip>{r.confidence} confidence</Chip></span></MuiTooltip>
            <MuiTooltip title={r.risk?.sizeWhy || ""}><span><Chip tone={r.risk?.size === "half" || r.risk?.size === "quarter" ? WARN : undefined}>{r.risk?.size} size</Chip></span></MuiTooltip>
            {r.eventNote ? <Chip tone={WARN}>print inside the expiry</Chip> : null}
            {r.conviction?.tv_gated ? <Chip tone={WARN}>valuation unreliable</Chip> : null}
            {r.completeness != null && r.completeness < 1 ? <Chip>{Math.round(r.completeness * 100)}% of inputs</Chip> : null}
          </div>

          <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-primary)", marginTop: 10, lineHeight: 1.55 }}>
            <strong>Action.</strong> {r.action.charAt(0).toUpperCase() + r.action.slice(1)}.
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 12, marginTop: 12 }}>
            <div style={{ border: "1px solid var(--color-border-secondary)", borderRadius: "var(--radius-md)", padding: "10px 12px", minWidth: 0 }}>
              <Gauge title="Conviction — is it worth owning?" value={r.conviction.score} hint="FA rating (0.70) and one valuation score (0.30): the equal blend of the true-value gap and the analyst-target gap after a 12% haircut. Fair value is not used — it already contains the true value." />
              {r.conviction.parts.map((p) => <Row key={p.name} name={p.name} score={p.score} reason={p.reason} onClick={p.tab ? go(p.tab) : undefined} />)}
              {r.conviction.quality_gate?.failed ? <div style={{ fontSize: "var(--font-size-xs)", color: WARN, marginTop: 6 }}>Quality gate: {r.conviction.quality_gate.why} — no Strong Buy.</div> : null}
            </div>
            <div style={{ border: "1px solid var(--color-border-secondary)", borderRadius: "var(--radius-md)", padding: "10px 12px", minWidth: 0 }}>
              <Gauge title="Timing — is now the moment?" value={r.timing.score} hint="Trend from price vs the 50/200-day averages (ties count as below, 0.5% tolerance), one mild ramped RSI rule, and the one-month move. Momentum is clipped to ±1 before it joins trend." />
              {r.timing.parts.length
                ? r.timing.parts.map((p) => <Row key={p.name} name={p.name} score={p.score} reason={p.reason} onClick={p.tab ? go(p.tab) : undefined} />)
                : <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-muted)", marginTop: 6 }}>No chart series — the verdict is capped at Hold until there is one.</div>}
            </div>
            <div style={{ border: "1px solid var(--color-border-secondary)", borderRadius: "var(--radius-md)", padding: "10px 12px", minWidth: 0 }}>
              <div style={{ fontSize: "var(--font-size-xs)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--color-text-muted)" }}>Risk — how big, and what's in the way?</div>
              <Row name="Implied volatility → size" reason={r.risk.sizeWhy} onClick={go("implied-volatility")} />
              {r.risk.event ? <Row name="Earnings" reason={r.risk.event.reason} onClick={go("earnings-review")} /> : null}
              {r.levels?.length ? (
                <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-muted)", marginTop: 6 }}>
                  <span style={{ fontWeight: 600, color: "var(--color-text-secondary)" }}>Levels: </span>
                  {r.levels.map((l) => `${l.name} ${Array.isArray(l.value) ? `${formatCurrency(l.value[0])}–${formatCurrency(l.value[1])}` : formatCurrency(l.value)}`).join(" · ")}
                </div>
              ) : null}
              {r.notes?.length ? <div style={{ fontSize: "var(--font-size-xs)", color: WARN, marginTop: 6 }}>{r.notes.join(" · ")}</div> : null}
            </div>
          </div>

          <details style={{ marginTop: 10 }}>
            <summary style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-secondary)", cursor: "pointer" }}>Full reasoning</summary>
            <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", marginTop: 6, lineHeight: 1.55 }}>{r.reason}</div>
          </details>
          <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-muted)", marginTop: 10 }}>
            A rules-based read of the models on this page, logged daily so its hit rate can be measured — a summary, not a forecast or advice. Click a line for its tab.
          </div>
        </>
      )}
    </div>
  );
};

export default FinalVerdict;
